/**
 * Display order + default-pick helpers for Financials sub-tabs.
 *
 * The priority list lives on the frontend because it's pure presentation —
 * which section should be the default landing tab, and in what order the
 * tabs should appear. Any section Chronograph returns that isn't in the
 * priority list is appended in alphabetical order.
 */
export const SECTION_DISPLAY_ORDER = [
  'P&L',
  'Balance Sheet',
  'Cash Flows',
  'KPIs',
] as const

const NON_FINANCIAL_SECTION_PREFIXES = ['KPIs'] as const

type RowWithSection = { globalSectionName?: string | null }

export function isSectionOrVariant(
  name: string,
  canonicalSection: string
): boolean {
  return name === canonicalSection || name.startsWith(`${canonicalSection} - `)
}

export function isFinancialStatementSectionName(
  name: string | null | undefined
): boolean {
  if (!name) return true

  return !NON_FINANCIAL_SECTION_PREFIXES.some(
    (prefix) => name === prefix || name.startsWith(`${prefix} - `)
  )
}

export function filterFinancialStatementRows<T extends RowWithSection>(
  rows: readonly T[]
): T[] {
  return rows.filter((row) =>
    isFinancialStatementSectionName(row.globalSectionName)
  )
}

export function orderSections(names: readonly string[]): string[] {
  const unique = Array.from(new Set(names))
  const seen = new Set<string>()
  const ordered: string[] = []

  // For each canonical prefix, push the prefix itself (if present) followed by
  // its subsidiary variants (e.g. "P&L - MBB"). This keeps related tabs next to
  // each other instead of scattering variants alphabetically.
  for (const prefix of SECTION_DISPLAY_ORDER) {
    if (unique.includes(prefix)) {
      ordered.push(prefix)
      seen.add(prefix)
    }
    const variants = unique
      .filter(
        (name) =>
          !seen.has(name) && name !== prefix && isSectionOrVariant(name, prefix)
      )
      .sort((a, b) => a.localeCompare(b))
    for (const variant of variants) {
      ordered.push(variant)
      seen.add(variant)
    }
  }

  const tail = unique
    .filter((n) => !seen.has(n))
    .sort((a, b) => a.localeCompare(b))
  return [...ordered, ...tail]
}

export function pickDefaultSection(
  orderedSections: readonly string[]
): string | null {
  // Relies on orderSections() placing the highest-priority section first
  // (P&L, then Balance Sheet, then Cash Flows, then KPIs), so the first
  // entry is the right default.
  return orderedSections[0] ?? null
}
